import {useQuery} from '@tanstack/react-query';
import {useMemo} from 'react';
import {fetchRest} from '../helpers/fetchRest';
import utils from '../helpers/utils';
import {TXM_DEFAULT_LOOK_BACK} from '../constants/durations';
import dateUtils from '../helpers/dateUtils';
import _get from 'lodash/get';

/**
 * get user transactions unified
 * @param {string} accountId - user account id to get transactions
 * @param {ProductFeatures} productFeatures - ProductFeatures
 * @param {boolean} hasInstitutions - boolean to determine if the user has institutions
 * @return {Object} returns users transactions, success and error fields
 */
const useTransactionsUnified = (accountId: string, productFeatures: ProductFeatures, hasInstitutions: boolean) => {
  // Stabilize transaction args per mount to prevent URL changes on every render
  // This ensures React Query can properly cache and identify the query
  const transactionArgs = useMemo(() => {
    const startDate = new Date(dateUtils.getStartDate(TXM_DEFAULT_LOOK_BACK)).toISOString();
    const endDate = new Date().toISOString();
    const hasTxnMonitoring = utils.isFeatureAvailable(productFeatures, 'transactionMonitoring');
    return {
      startDate,
      endDate,
      sortBy: 'transactionDate',
      sortOrder: 'desc',
      count: '30',
      status: hasTxnMonitoring ? 'POSTED,PENDING' : 'POSTED',
    };
  }, [productFeatures]);

  // Build URL from stable params
  const queryParam = useMemo(() => new URLSearchParams(transactionArgs).toString(), [transactionArgs]);
  const url = useMemo(() => utils.getTransactionsUnifiedPath(queryParam), [queryParam]);

  // Keep the same gating logic as unified components:
  // - Plaid/unified UI path: controlled by REACT_APP_SHOW_UNIFIED_TRANSACTIONS + financialWellness availability
  // - Yodlee UDM migration path: controlled by REACT_APP_ENABLE_YODLEE_UDM + txm-related features
  const featureStatusFw = _get(productFeatures, 'financialWellness.featureStatus', null);
  const isUnifiedTransactionsEnabled =
    (window.REACT_APP_SHOW_UNIFIED_TRANSACTIONS && featureStatusFw === 'AVAILABLE') ||
    (window.REACT_APP_ENABLE_YODLEE_UDM &&
      (utils.isFeatureAvailable(productFeatures, 'financialMonitoringUk') ||
        utils.isFeatureAvailable(productFeatures, 'transactionMonitoring')));

  const query = useQuery({
    queryKey: ['transactionsUnified', accountId],
    queryFn: () =>
      fetchRest(url, {
        method: 'GET',
        headers: {Accept: 'application/json', account_id: accountId},
      }),
    enabled: !!accountId && isUnifiedTransactionsEnabled && hasInstitutions,
  });
  return {...query, isLoading: query.isPending};
};

export default useTransactionsUnified;
